import Phaser from 'phaser';
import { sounds } from '../audio/SoundEffects';
import { progression } from '../game/Progression';
import { ShopModal } from '../ui/ShopModal';

export class VictoryScene extends Phaser.Scene {
  constructor() {
    super({ key: 'VictoryScene' });
  }

  init(data) {
    this.completedLevel = data.level || 1;
    this.finalScore = data.score || 0;
    this.finalVinyls = data.vinyls || 0;
    this.bankGain = data.bankGain || data.vinyls || 0;
    this.bankTotal = data.bankTotal || progression.getBankVinyls();
  }

  create() {
    const { width, height } = this.scale;

    // Victory celebration sound
    sounds.playRockChord();

    // Dark majestic background
    const bg = this.add.graphics();
    bg.fillGradientStyle(0x0f172a, 0x0f172a, 0x1e1b4b, 0x1e1b4b, 1);
    bg.fillRect(0, 0, width, height);

    // Confetti / note particles
    const emitter = this.add.particles(width / 2, 100, 'particle_note', {
      lifespan: 1400,
      speed: { min: 100, max: 260 },
      angle: { min: 0, max: 360 },
      scale: { start: 1.5, end: 0.2 },
      frequency: 120
    });

    const isFinalStage = this.completedLevel === 11;

    // Title
    const title = this.add.text(width / 2, 130, isFinalStage ? '👑 SUPREME CREATOR OF CHAOS! 👑' : '⚡ STAGE CLEAR! ⚡', {
      fontFamily: 'Impact, Arial Black, sans-serif',
      fontSize: isFinalStage ? '28px' : '44px',
      color: '#facc15',
      stroke: '#b45309',
      strokeThickness: 8,
      align: 'center'
    }).setOrigin(0.5);

    this.tweens.add({
      targets: title,
      scaleX: 1.08,
      scaleY: 1.08,
      duration: 600,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut'
    });

    let subtitleText = 'Olympus Arena conquered! Ready for Tartarus?';
    if (this.completedLevel === 2) {
      subtitleText = 'Tartarus Abyss survived! Hephaestus Forge awaits!';
    } else if (this.completedLevel === 3) {
      subtitleText = 'Cyber Forge cleared! Face the Cosmic Apotheosis!';
    } else if (this.completedLevel === 4) {
      subtitleText = 'Cosmic Apotheosis conquered! Dive into Atlantis Neon Abyss!';
    } else if (this.completedLevel === 5) {
      subtitleText = 'Atlantis Abyss traversed! Join Dionysus Neon Rave!';
    } else if (this.completedLevel === 6) {
      subtitleText = 'Dionysus Rave conquered! Infiltrate Ares War Citadel!';
    } else if (this.completedLevel === 7) {
      subtitleText = 'Ares Citadel destroyed! Confront the Kronos Chrono-Singularity!';
    } else if (this.completedLevel === 8) {
      subtitleText = 'Kronos Singularity harnessed! Cross the Spectral River Styx!';
    } else if (this.completedLevel === 9) {
      subtitleText = 'River Styx traversed! Ignite Apollo Solar Hyper-Drive!';
    } else if (this.completedLevel === 10) {
      subtitleText = 'Helios Hyper-Drive maxed out! Enter Primordial Chaos Genesis!';
    } else if (isFinalStage) {
      subtitleText = 'You conquered all 11 cyber-mythic dimensions and forged the Rock Cosmos!';
    }

    this.add.text(width / 2, 185, subtitleText, {
      fontFamily: 'Arial, sans-serif',
      fontSize: '13px',
      color: '#38bdf8',
      fontStyle: 'bold',
      align: 'center'
    }).setOrigin(0.5);

    // Score Board Panel
    const panel = this.add.graphics();
    panel.fillStyle(0x1e293b, 0.92);
    panel.fillRoundedRect(width / 2 - 165, 220, 330, 230, 16);
    panel.lineStyle(3, 0xf59e0b, 0.9);
    panel.strokeRoundedRect(width / 2 - 165, 220, 330, 230, 16);

    this.add.text(width / 2, 255, `STAGE SCORE: ${this.finalScore}`, {
      fontFamily: 'Impact, Arial Black, sans-serif',
      fontSize: '30px',
      color: '#facc15'
    }).setOrigin(0.5);

    this.add.text(width / 2, 300, `📀 RUN REWARD: +${this.bankGain} VINYLS`, {
      fontFamily: 'Arial, sans-serif',
      fontSize: '16px',
      color: '#4ade80',
      fontStyle: 'bold'
    }).setOrigin(0.5);

    this.txtBank = this.add.text(width / 2, 345, `🏦 BANK TOTAL: ${progression.getBankVinyls()} VINYLS`, {
      fontFamily: 'Arial, sans-serif',
      fontSize: '16px',
      color: '#ffffff',
      fontStyle: 'bold'
    }).setOrigin(0.5);

    const highScore = localStorage.getItem('zelvis_highscore') || '0';
    this.add.text(width / 2, 395, `🏆 ALL-TIME BEST: ${highScore}`, {
      fontFamily: 'Arial, sans-serif',
      fontSize: '15px',
      color: '#38bdf8',
      fontStyle: 'bold'
    }).setOrigin(0.5);

    // Action Buttons
    if (!isFinalStage) {
      const nextLevelNum = this.completedLevel + 1;
      const nextLabels = {
        2: '⚡ NEXT: TARTARUS ⚡',
        3: '⚡ NEXT: CYBER FORGE ⚡',
        4: '⚡ NEXT: COSMIC OLYMPUS ⚡',
        5: '⚡ NEXT: ATLANTIS ABYSS ⚡',
        6: '⚡ NEXT: DIONYSUS RAVE ⚡',
        7: '⚡ NEXT: ARES CITADEL ⚡',
        8: '⚡ NEXT: KRONOS CLIMAX ⚡',
        9: '⚡ NEXT: RIVER STYX ⚡',
        10: '⚡ NEXT: APOLLO SOLAR ⚡',
        11: '⚡ NEXT: CHAOS GENESIS ⚡'
      };
      const nextColors = {
        2: { bg: 0x9333ea, stroke: 0xe879f9 },
        3: { bg: 0xc2410c, stroke: 0xf97316 },
        4: { bg: 0x4f46e5, stroke: 0x38bdf8 },
        5: { bg: 0x0891b2, stroke: 0x22d3ee },
        6: { bg: 0x9333ea, stroke: 0xfacc15 },
        7: { bg: 0xb91c1c, stroke: 0xef4444 },
        8: { bg: 0xb45309, stroke: 0xfde047 },
        9: { bg: 0x059669, stroke: 0x34d399 },
        10: { bg: 0xd97706, stroke: 0xfbbf24 },
        11: { bg: 0x7c3aed, stroke: 0xc084fc }
      };

      const btnColors = nextColors[nextLevelNum] || { bg: 0x9333ea, stroke: 0xe879f9 };
      const btnLabel = nextLabels[nextLevelNum] || '⚡ NEXT STAGE ⚡';

      // NEXT STAGE BUTTON
      const nextBtn = this.add.container(width / 2, height - 235);
      const nBg = this.add.rectangle(0, 0, 280, 56, btnColors.bg, 1);
      nBg.setStrokeStyle(3, btnColors.stroke, 1);
      nBg.setInteractive({ useHandCursor: true });

      const nTxt = this.add.text(0, 0, btnLabel, {
        fontFamily: 'Impact, Arial Black, sans-serif',
        fontSize: '22px',
        color: '#ffffff'
      }).setOrigin(0.5);

      nextBtn.add([nBg, nTxt]);

      const onNext = () => {
        sounds.playRockChord();
        this.scene.start('GameScene', {
          level: nextLevelNum,
          score: this.finalScore,
          vinyls: this.finalVinyls
        });
      };

      nBg.on('pointerdown', onNext);
      nTxt.setInteractive({ useHandCursor: true }).on('pointerdown', onNext);

      this.tweens.add({
        targets: nextBtn,
        scaleX: 1.05,
        scaleY: 1.05,
        duration: 600,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut'
      });
    }

    // SHOP BUTTON
    const shopBtn = this.add.container(width / 2, height - (isFinalStage ? 220 : 165));
    const sBg = this.add.rectangle(0, 0, 280, 48, 0xb45309, 1);
    sBg.setStrokeStyle(2, 0xfacc15, 1);
    sBg.setInteractive({ useHandCursor: true });

    const sTxt = this.add.text(0, 0, '🛍️ ROCKSTAR SHOP & PERKS', {
      fontFamily: 'Arial, sans-serif',
      fontSize: '15px',
      color: '#ffffff',
      fontStyle: 'bold'
    }).setOrigin(0.5);

    shopBtn.add([sBg, sTxt]);

    const onOpenShop = () => {
      new ShopModal(this, () => {
        if (this.txtBank) {
          this.txtBank.setText(`🏦 BANK TOTAL: ${progression.getBankVinyls()} VINYLS`);
        }
      });
    };

    sBg.on('pointerdown', onOpenShop);
    sTxt.setInteractive({ useHandCursor: true }).on('pointerdown', onOpenShop);

    // MAIN MENU BUTTON
    const menuBtn = this.add.container(width / 2, height - (isFinalStage ? 140 : 100));
    const mBg = this.add.rectangle(0, 0, 240, 44, 0x334155, 0.95);
    mBg.setStrokeStyle(2, 0x64748b, 1);
    mBg.setInteractive({ useHandCursor: true });

    const mTxt = this.add.text(0, 0, 'MAIN MENU', {
      fontFamily: 'Arial, sans-serif',
      fontSize: '16px',
      color: '#f8fafc',
      fontStyle: 'bold'
    }).setOrigin(0.5);

    menuBtn.add([mBg, mTxt]);

    const onMenu = () => {
      this.scene.start('MenuScene');
    };

    mBg.on('pointerdown', onMenu);
    mTxt.setInteractive({ useHandCursor: true }).on('pointerdown', onMenu);

    this.input.keyboard.on('keydown-SPACE', () => {
      if (!isFinalStage) {
        this.scene.start('GameScene', {
          level: this.completedLevel + 1,
          score: this.finalScore,
          vinyls: this.finalVinyls
        });
      } else {
        this.scene.start('MenuScene');
      }
    });
  }
}

